/**
 * useIsTouch — Phase 2d (vibe-designer 1.127.0).
 *
 * Side-effect hook that reads `window.matchMedia('(pointer: coarse)').matches`
 * at mount and writes it into uiStore.isTouch. Subscribes to media-query
 * 'change' events so Chrome DevTools "Toggle device toolbar" works mid-
 * session — real touch hardware never flips, but DevTools testing matters.
 *
 * Returns the current isTouch value as a convenience; callers can also
 * read it directly from `useUIStore`.
 *
 * Graceful no-op when `window.matchMedia` is undefined (older browsers,
 * jsdom default). The hook does not throw and store stays at its default.
 *
 * Uses both `addEventListener` (modern) and `addListener` (legacy) for
 * MediaQueryList — Safari < 14 only had the legacy `addListener` API.
 */
import { useEffect } from 'react';
import { useUIStore } from '@/stores/uiStore';

type LegacyMQL = MediaQueryList & {
  addListener?: (handler: (e: MediaQueryListEvent) => void) => void;
  removeListener?: (handler: (e: MediaQueryListEvent) => void) => void;
};

export function useIsTouch(): boolean {
  const isTouch = useUIStore((s) => s.isTouch);
  const setIsTouch = useUIStore((s) => s.setIsTouch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)') as LegacyMQL;
    setIsTouch(mq.matches);

    const handler = (e: MediaQueryListEvent) => setIsTouch(e.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(handler);
    }

    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', handler);
      } else if (typeof mq.removeListener === 'function') {
        mq.removeListener(handler);
      }
    };
  }, [setIsTouch]);

  return isTouch;
}
